'use client';

import { useEffect, useState } from 'react';
import ArrowLabel from '@/components/ui/ArrowLabel';

export type NotFoundMessages = { title: string; desc: string; back: string };
export type NotFoundLocale = 'pl' | 'en' | 'ua' | 'ru';

// "ua" route segment is Ukrainian → ISO 639-1 "uk" for the html lang attribute.
const HTML_LANG: Record<NotFoundLocale, string> = { pl: 'pl', en: 'en', ua: 'uk', ru: 'ru' };

// The global 404 must stay statically renderable: reading the NEXT_LOCALE
// cookie or Accept-Language on the server (next/headers) marks the not-found
// boundary dynamic, and Next then silently demotes EVERY page that embeds the
// boundary from SSG to per-request SSR. So the server renders Polish, and this
// client component swaps in the visitor's locale after mount (pure UX — the 404
// status itself keeps the page out of search).
function resolveLocale(messages: Record<NotFoundLocale, NotFoundMessages>): NotFoundLocale {
  const cookieLocale = document.cookie
    .split('; ')
    .find((c) => c.startsWith('NEXT_LOCALE='))
    ?.split('=')[1];
  if (cookieLocale && cookieLocale in messages) return cookieLocale as NotFoundLocale;

  const primary = (navigator.language || '').split('-')[0].toLowerCase();
  if (primary === 'uk') return 'ua';
  if (primary in messages) return primary as NotFoundLocale;
  return 'pl';
}

export default function NotFoundContent({
  messages,
}: {
  messages: Record<NotFoundLocale, NotFoundMessages>;
}) {
  const [locale, setLocale] = useState<NotFoundLocale>('pl');

  useEffect(() => {
    const resolved = resolveLocale(messages);
    setLocale(resolved);
    document.documentElement.lang = HTML_LANG[resolved];
  }, [messages]);

  const t = messages[locale];
  const homeHref = locale === 'pl' ? '/' : `/${locale}`;

  return (
    <main className="relative flex h-[100dvh] flex-col items-center justify-center px-margin-mobile text-center lg:px-margin-desktop">
      {/* Oversized, muted numeral — tonal layering, no shadows (design system). */}
      <p className="font-headline text-[4rem] font-bold leading-none tracking-tight text-outline-variant/50 short:text-[3.5rem] sm:text-[7rem]">
        404
      </p>
      <h1 className="mt-stack-md font-headline text-headline-lg font-bold tracking-[0.02em] text-on-surface">
        {t.title}
      </h1>
      <p className="mt-stack-md max-w-md text-body-md text-on-surface-variant md:text-body-lg">
        {t.desc}
      </p>

      {/* Primary pill CTA → home, with the slide-in arrow used across the site. */}
      <a
        href={homeHref}
        className="group mt-stack-lg inline-flex items-center justify-center rounded-full bg-on-surface px-8 py-4 font-headline text-body-md font-bold text-background transition-all duration-200 hover:opacity-90 active:scale-95 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 focus-visible:ring-offset-background"
      >
        <ArrowLabel>{t.back}</ArrowLabel>
      </a>
    </main>
  );
}
